// Long-form, SEO-oriented editorial copy shown under the home-page carousels
// (see src/components/riviera/main-content.tsx). It's trilingual, evergreen and
// hand-authored — it changes rarely, so it lives here as static data rather
// than in Sanity or the i18n resource JSON (which is for short UI strings).
//
// Each locale is an ordered list of blocks. Inline runs let a paragraph mix
// plain text, bold emphasis and links. Links only point at pages that are
// always exported — the catalogue hubs and the seven city guides — never at an
// individual tour/event/boat: listings leave the feeds and are purged, and this
// copy outlives them. The active locale's blocks are rendered under the current
// /[lang] prefix, so the links stay in-language.
//
// Describe only what the site actually has (no restaurant booking, no Italian
// Riviera), and nothing that drifts — counts, prices, a given season's line-up.

import type { Locale } from '@todo/core/i18n/locales';

/** A catalogue hub, or one city's guide (/[lang]/[city]). */
export type LinkTarget = 'events' | 'tours' | 'wine-tours' | 'boats' | 'articles' | { city: string };

export type ContentLink = { text: string; to: LinkTarget };

/** One piece of a paragraph or list item: text, **bold** text, or a link. */
export type Inline = string | { bold: string } | { link: ContentLink };

export type ContentBlock =
  | { type: 'heading'; level: 2 | 3 | 4; text: string }
  | { type: 'paragraph'; runs: Inline[] }
  | { type: 'list'; items: Inline[][] };

export const homeContent: Record<Locale, ContentBlock[]> = {
  en: [
    { type: 'heading', level: 2, text: 'Things to do on the French Riviera' },
    {
      type: 'paragraph',
      runs: [
        "The French Riviera — the Côte d'Azur — runs from Menton on the Italian border to Saint-Tropez: a coast of Belle Époque towns, hilltop villages and turquoise bays, with the foothills of the Alps rising behind it. ",
        { bold: 'Riviera To-Do' },
        " brings what there is to do here into one place — what's on, which tours are worth booking, where to taste wine and how to get out on the water.",
      ],
    },
    { type: 'heading', level: 3, text: "What you'll find on Riviera To-Do" },
    {
      type: 'list',
      items: [
        [
          { link: { text: 'Events', to: 'events' } },
          ': concerts, theatre, stand-up and festivals across the Riviera, with every date and a link to tickets — updated daily.',
        ],
        [
          { link: { text: 'Tours and day trips', to: 'tours' } },
          ' from Nice, Cannes, Monaco, Antibes and Saint-Tropez, ranked by traveller reviews.',
        ],
        [
          { link: { text: 'Wine tours', to: 'wine-tours' } },
          ': tastings in the Bellet vineyards above Nice and the rosé estates of the Côtes de Provence.',
        ],
        [
          { link: { text: 'Boat and yacht rental', to: 'boats' } },
          ': motorboats, RIBs, yachts and catamarans in every port, with a skipper or without.',
        ],
        [
          { link: { text: 'Articles', to: 'articles' } },
          ': seasonal guides, festivals, food and wine, and practical tips for planning your trip.',
        ],
      ],
    },
    { type: 'heading', level: 3, text: 'Gastronomic to-do: what to try' },
    {
      type: 'paragraph',
      runs: [
        'Riviera cuisine is the Mediterranean diet at its simplest: fresh seafood, good olive oil, local vegetables and Provençal herbs. No Riviera to-do list is complete without it — make sure you try these local favourites:',
      ],
    },
    {
      type: 'list',
      items: [
        [
          { bold: 'Salade niçoise: ' },
          'the classic salad of Nice — fresh vegetables, tuna or anchovies, boiled eggs and black olives.',
        ],
        [
          { bold: 'Socca: ' },
          "a thin, crispy chickpea-flour pancake baked in a wood-fired oven and sold by the slice in Nice's old town — the perfect street-food snack.",
        ],
        [
          { bold: 'Pissaladière and pan bagnat: ' },
          'an onion-and-anchovy tart and a salade niçoise in a round bun — two more Niçois classics for a picnic by the sea.',
        ],
        [
          { bold: 'Bouillabaisse: ' },
          'the rich fish stew of the Provençal coast, traditionally served with croutons and a garlicky saffron rouille.',
        ],
      ],
    },
    {
      type: 'paragraph',
      runs: [
        "The markets are the best place to start: the Cours Saleya in Nice and the Marché Provençal in Antibes. Food walks through the old towns are among the most popular ",
        { link: { text: 'tours on the Riviera', to: 'tours' } },
        '.',
      ],
    },
    { type: 'heading', level: 3, text: 'The Riviera in motion' },
    { type: 'heading', level: 4, text: 'On the water' },
    {
      type: 'paragraph',
      runs: [
        "Getting out on the water is one of the defining Riviera experiences: a boat to the Lérins Islands off Cannes, around Cap d'Antibes or into the bay of Villefranche, a day at Pampelonne off Saint-Tropez, or a sailing regatta watched from the quay. ",
        { link: { text: 'Rent a boat or yacht', to: 'boats' } },
        ' with a skipper or on your own.',
      ],
    },
    { type: 'heading', level: 4, text: 'Hiking and cycling' },
    {
      type: 'paragraph',
      runs: [
        { bold: 'For walkers, the region has hundreds of kilometres of scenic trails' },
        " — from the coastal paths of Cap Ferrat and Cap d'Antibes and the Nietzsche Path up to Èze, to mountain routes in the Mercantour National Park. Cyclists come from all over the world for the winding roads above the sea and the climbs of the Alpine foothills.",
      ],
    },
    { type: 'heading', level: 4, text: 'Golf' },
    {
      type: 'paragraph',
      runs: [
        { bold: 'The golf courses around Mougins and in the hills behind Cannes' },
        ' are laid out among pine forests, with the sea in view.',
      ],
    },
    { type: 'heading', level: 3, text: 'Riviera wine and the perfumes of Grasse' },
    {
      type: 'paragraph',
      runs: [
        "The Riviera borders Provence, home of the light, dry rosé that suits a summer by the sea. The rosé estates of the Côtes de Provence are less than an hour from the coast, and Nice even has its own tiny appellation — Bellet, one of France's oldest, in the hills just above the city. ",
        { link: { text: 'Wine tours on the French Riviera', to: 'wine-tours' } },
        ' run from workshops in town to full days among the vineyards.',
      ],
    },
    {
      type: 'paragraph',
      runs: [
        'Inland from Cannes, Grasse is ',
        { bold: 'the birthplace of French perfumery' },
        ': its historic perfume houses open their workshops to visitors, and many ',
        { link: { text: 'day trips', to: 'tours' } },
        ' from Nice and Cannes combine a perfumery with wine tasting and the hilltop villages of Provence.',
      ],
    },
    { type: 'heading', level: 3, text: 'Discover the towns of the French Riviera' },
    {
      type: 'paragraph',
      runs: [
        'From Menton to Saint-Tropez, each town on the coast has its own character — Belle Époque villas and superyachts, old ports and hilltop villages, all between the Alps and the Mediterranean.',
      ],
    },
    { type: 'heading', level: 4, text: 'Nice: the cultural capital' },
    {
      type: 'paragraph',
      runs: [
        'Nice balances laid-back beach life with the energy of a real city. A stroll along the Promenade des Anglais is only the start: explore the narrow streets of the Old Town (Vieux Nice), the flower market on the Cours Saleya and the local bistros, and the museums dedicated to Marc Chagall and Henri Matisse. ',
        { link: { text: 'Things to do in Nice', to: { city: 'nice' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Cannes: cinema and the Croisette' },
    {
      type: 'paragraph',
      runs: [
        "Known around the world for its film festival, Cannes has much more than red carpets. The palm-lined Croisette, with its legendary hotels, leads to the old port and the hill of Le Suquet; a short ferry ride away, the Lérins Islands and their ancient abbey are an oasis of calm. ",
        { link: { text: 'Things to do in Cannes', to: { city: 'cannes' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Monaco: glamour on the Rock' },
    {
      type: 'paragraph',
      runs: [
        "The Prince's Palace and the old town on the Rock, the Oceanographic Museum, the Monte-Carlo Casino and the corners of the Formula 1 circuit make this tiny principality an essential stop on any Riviera itinerary. ",
        { link: { text: 'Things to do in Monaco', to: { city: 'monaco' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: "Antibes: old town and Cap d'Antibes" },
    {
      type: 'paragraph',
      runs: [
        "Antibes pairs a walled old town and a Picasso museum in the Château Grimaldi with Port Vauban, one of the Mediterranean's great superyacht harbours. Around the cape lie the coastal path of Cap d'Antibes and the beaches and jazz of Juan-les-Pins. ",
        { link: { text: 'Things to do in Antibes', to: { city: 'antibes' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Saint-Tropez: bohemian charm' },
    {
      type: 'paragraph',
      runs: [
        'Once a modest fishing village, Saint-Tropez became a favourite of artists and celebrities, yet it keeps its Provençal spirit: the 16th-century citadel, the cafés around the port and the long sandy beaches of Pampelonne. ',
        { link: { text: 'Things to do in Saint-Tropez', to: { city: 'sttropez' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Menton and Villefranche-sur-Mer' },
    {
      type: 'paragraph',
      runs: [
        "Menton, the last town before Italy, is known for its mild climate, its lemons and the Fête du Citron each February, and an Italian-looking old town climbing the hill. Villefranche-sur-Mer sits on one of the Riviera's deepest bays, with a citadel and a fishermen's chapel painted by Jean Cocteau. ",
        { link: { text: 'Things to do in Menton', to: { city: 'menton' } } },
        ' · ',
        { link: { text: 'Things to do in Villefranche-sur-Mer', to: { city: 'villefranche' } } },
        '.',
      ],
    },
  ],

  fr: [
    { type: 'heading', level: 2, text: "Que faire sur la Côte d'Azur" },
    {
      type: 'paragraph',
      runs: [
        "La Côte d'Azur s'étend de Menton, à la frontière italienne, jusqu'à Saint-Tropez : une côte de villes Belle Époque, de villages perchés et de criques turquoise, adossée aux contreforts des Alpes. ",
        { bold: 'Riviera To-Do' },
        " réunit en un seul endroit ce qu'il y a à faire ici : l'agenda des sorties, les excursions qui valent le détour, les dégustations de vin et les sorties en mer.",
      ],
    },
    { type: 'heading', level: 3, text: 'Ce que vous trouverez sur Riviera To-Do' },
    {
      type: 'list',
      items: [
        [
          { link: { text: "L'agenda", to: 'events' } },
          ' : concerts, théâtre, humour et festivals sur toute la Riviera, avec toutes les dates et un lien vers la billetterie — mis à jour chaque jour.',
        ],
        [
          { link: { text: 'Les excursions et visites', to: 'tours' } },
          ' au départ de Nice, Cannes, Monaco, Antibes et Saint-Tropez, classées selon les avis des voyageurs.',
        ],
        [
          { link: { text: 'Les excursions œnologiques', to: 'wine-tours' } },
          ' : dégustations dans les vignes de Bellet, au-dessus de Nice, et chez les domaines de rosé des Côtes de Provence.',
        ],
        [
          { link: { text: 'La location de bateaux et de yachts', to: 'boats' } },
          ' : bateaux à moteur, semi-rigides, yachts et catamarans dans chaque port, avec ou sans skipper.',
        ],
        [
          { link: { text: 'Les articles', to: 'articles' } },
          ' : guides de saison, festivals, vins et gastronomie, conseils pratiques pour préparer votre séjour.',
        ],
      ],
    },
    { type: 'heading', level: 3, text: "Gastronomie : ce qu'il faut goûter" },
    {
      type: 'paragraph',
      runs: [
        "La cuisine de la Riviera, c'est le régime méditerranéen dans sa forme la plus simple : produits de la mer, bonne huile d'olive, légumes du pays et herbes de Provence. Pas de séjour réussi sans y goûter — à commencer par ces spécialités :",
      ],
    },
    {
      type: 'list',
      items: [
        [
          { bold: 'La salade niçoise : ' },
          "le classique de Nice — légumes frais, thon ou anchois, œufs durs et olives noires.",
        ],
        [
          { bold: 'La socca : ' },
          'une fine galette de farine de pois chiches cuite au feu de bois et vendue à la part dans le Vieux-Nice — le snack de rue idéal.',
        ],
        [
          { bold: 'La pissaladière et le pan bagnat : ' },
          "une tarte aux oignons et aux anchois et une salade niçoise dans un pain rond — deux autres classiques niçois pour pique-niquer face à la mer.",
        ],
        [
          { bold: 'La bouillabaisse : ' },
          'la soupe de poissons de la côte provençale, servie avec des croûtons et une rouille à l’ail et au safran.',
        ],
      ],
    },
    {
      type: 'paragraph',
      runs: [
        "Les marchés sont le meilleur point de départ : le cours Saleya à Nice et le Marché provençal d'Antibes. Les balades gourmandes dans les vieilles villes font partie des ",
        { link: { text: 'excursions les plus prisées de la Riviera', to: 'tours' } },
        '.',
      ],
    },
    { type: 'heading', level: 3, text: 'La Riviera en mouvement' },
    { type: 'heading', level: 4, text: 'En mer' },
    {
      type: 'paragraph',
      runs: [
        "Prendre la mer fait partie de l'expérience de la Riviera : cap sur les îles de Lérins au large de Cannes, le tour du cap d'Antibes ou la rade de Villefranche, une journée à Pampelonne au large de Saint-Tropez, ou une régate admirée depuis le quai. ",
        { link: { text: 'Louez un bateau ou un yacht', to: 'boats' } },
        ', avec skipper ou en autonomie.',
      ],
    },
    { type: 'heading', level: 4, text: 'Randonnée et vélo' },
    {
      type: 'paragraph',
      runs: [
        { bold: 'Les marcheurs disposent de centaines de kilomètres de sentiers' },
        " — des sentiers du littoral du cap Ferrat et du cap d'Antibes et du chemin de Nietzsche qui monte à Èze, jusqu'aux itinéraires de montagne du parc national du Mercantour. Les cyclistes viennent du monde entier pour les routes en lacets au-dessus de la mer et les cols des contreforts alpins.",
      ],
    },
    { type: 'heading', level: 4, text: 'Golf' },
    {
      type: 'paragraph',
      runs: [
        { bold: "Les golfs autour de Mougins et dans les collines derrière Cannes" },
        ' sont tracés au milieu des pinèdes, avec vue sur la mer.',
      ],
    },
    { type: 'heading', level: 3, text: 'Les vins de la Riviera et les parfums de Grasse' },
    {
      type: 'paragraph',
      runs: [
        "La Riviera borde la Provence, patrie du rosé sec et léger qui accompagne si bien l'été au bord de la mer. Les domaines des Côtes de Provence sont à moins d'une heure de la côte, et Nice possède même sa propre petite appellation, Bellet — l'une des plus anciennes de France, sur les collines au-dessus de la ville. ",
        { link: { text: "Les excursions œnologiques sur la Côte d'Azur", to: 'wine-tours' } },
        ' vont de l’atelier de dégustation en ville à la journée complète dans les vignes.',
      ],
    },
    {
      type: 'paragraph',
      runs: [
        "Dans l'arrière-pays cannois, Grasse est ",
        { bold: 'le berceau de la parfumerie française' },
        ' : ses maisons historiques ouvrent leurs ateliers aux visiteurs, et de nombreuses ',
        { link: { text: 'excursions à la journée', to: 'tours' } },
        ' au départ de Nice et de Cannes associent une parfumerie, une dégustation de vin et les villages perchés de Provence.',
      ],
    },
    { type: 'heading', level: 3, text: "Découvrir les villes de la Côte d'Azur" },
    {
      type: 'paragraph',
      runs: [
        "De Menton à Saint-Tropez, chaque ville de la côte a son caractère — villas Belle Époque et superyachts, vieux ports et villages perchés, entre les Alpes et la Méditerranée.",
      ],
    },
    { type: 'heading', level: 4, text: 'Nice : la capitale culturelle' },
    {
      type: 'paragraph',
      runs: [
        "Nice concilie la douceur de la plage et l'énergie d'une vraie ville. La promenade des Anglais n'est qu'un début : flânez dans les ruelles du Vieux-Nice, au marché aux fleurs du cours Saleya et dans les bistrots locaux, et visitez les musées consacrés à Marc Chagall et à Henri Matisse. ",
        { link: { text: 'Que faire à Nice', to: { city: 'nice' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Cannes : le cinéma et la Croisette' },
    {
      type: 'paragraph',
      runs: [
        "Célèbre dans le monde entier pour son festival, Cannes ne se résume pas au tapis rouge. La Croisette bordée de palmiers et de palaces mène au vieux port et à la colline du Suquet ; à quelques minutes de bateau, les îles de Lérins et leur abbaye offrent une parenthèse de calme. ",
        { link: { text: 'Que faire à Cannes', to: { city: 'cannes' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Monaco : le prestige sur le Rocher' },
    {
      type: 'paragraph',
      runs: [
        "Le Palais princier et la vieille ville sur le Rocher, le Musée océanographique, le Casino de Monte-Carlo et les virages du circuit de Formule 1 font de la principauté une étape incontournable de tout séjour sur la Riviera. ",
        { link: { text: 'Que faire à Monaco', to: { city: 'monaco' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: "Antibes : la vieille ville et le cap d'Antibes" },
    {
      type: 'paragraph',
      runs: [
        "Antibes associe une vieille ville fortifiée et le musée Picasso du château Grimaldi à Port Vauban, l'un des grands ports de superyachts de Méditerranée. Autour du cap : le sentier du littoral du cap d'Antibes, les plages et le jazz de Juan-les-Pins. ",
        { link: { text: 'Que faire à Antibes', to: { city: 'antibes' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Saint-Tropez : le charme bohème' },
    {
      type: 'paragraph',
      runs: [
        "Ancien village de pêcheurs devenu le refuge des artistes et des célébrités, Saint-Tropez a gardé son âme provençale : la citadelle du XVIᵉ siècle, les cafés du port et les longues plages de sable de Pampelonne. ",
        { link: { text: 'Que faire à Saint-Tropez', to: { city: 'sttropez' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Menton et Villefranche-sur-Mer' },
    {
      type: 'paragraph',
      runs: [
        "Menton, dernière ville avant l'Italie, est connue pour son climat doux, ses citrons et la Fête du Citron chaque février, et pour sa vieille ville aux airs italiens accrochée à la colline. Villefranche-sur-Mer borde l'une des rades les plus profondes de la Riviera, avec sa citadelle et une chapelle des pêcheurs décorée par Jean Cocteau. ",
        { link: { text: 'Que faire à Menton', to: { city: 'menton' } } },
        ' · ',
        { link: { text: 'Que faire à Villefranche-sur-Mer', to: { city: 'villefranche' } } },
        '.',
      ],
    },
  ],

  ru: [
    { type: 'heading', level: 2, text: 'Чем заняться на Лазурном Берегу' },
    {
      type: 'paragraph',
      runs: [
        'Лазурный Берег (Французская Ривьера) тянется от Ментона у итальянской границы до Сен-Тропе: города эпохи Belle Époque, деревни на скалах и бирюзовые бухты, а за ними — предгорья Альп. ',
        { bold: 'Riviera To-Do' },
        ' собирает в одном месте всё, чем здесь можно заняться: афишу мероприятий, экскурсии, которые стоит забронировать, винные дегустации и выходы в море.',
      ],
    },
    { type: 'heading', level: 3, text: 'Что есть на Riviera To-Do' },
    {
      type: 'list',
      items: [
        [
          { link: { text: 'Афиша', to: 'events' } },
          ': концерты, спектакли, стендап и фестивали по всей Ривьере — со всеми датами и ссылкой на билеты, обновляется каждый день.',
        ],
        [
          { link: { text: 'Экскурсии и однодневные поездки', to: 'tours' } },
          ' из Ниццы, Канн, Монако, Антиба и Сен-Тропе — отсортированные по отзывам путешественников.',
        ],
        [
          { link: { text: 'Винные туры', to: 'wine-tours' } },
          ': дегустации на виноградниках Белле над Ниццей и в хозяйствах розовых вин Кот-де-Прованс.',
        ],
        [
          { link: { text: 'Аренда яхт и лодок', to: 'boats' } },
          ': моторные лодки, РИБы, яхты и катамараны во всех портах — с капитаном или без.',
        ],
        [
          { link: { text: 'Статьи', to: 'articles' } },
          ': сезонные гиды, фестивали, вино и еда, практические советы для поездки.',
        ],
      ],
    },
    { type: 'heading', level: 3, text: 'Гастрономия: что обязательно попробовать' },
    {
      type: 'paragraph',
      runs: [
        'Кухня Ривьеры — средиземноморская диета в самом простом виде: свежие морепродукты, хорошее оливковое масло, местные овощи и прованские травы. Поездка будет неполной без неё — начните с этих местных блюд:',
      ],
    },
    {
      type: 'list',
      items: [
        [
          { bold: 'Салат нисуаз: ' },
          'классика Ниццы — свежие овощи, тунец или анчоусы, варёные яйца и чёрные оливки.',
        ],
        [
          { bold: 'Сокка: ' },
          'тонкая хрустящая лепёшка из нутовой муки, которую пекут в дровяной печи и продают кусками в старой Ницце, — идеальный стритфуд.',
        ],
        [
          { bold: 'Писсаладьер и пан-баньят: ' },
          'пирог с луком и анчоусами и салат нисуаз в круглой булке — ещё две ниццкие классики для пикника у моря.',
        ],
        [
          { bold: 'Буйабес: ' },
          'наваристая рыбная похлёбка прованского побережья, которую подают с гренками и чесночным соусом руй с шафраном.',
        ],
      ],
    },
    {
      type: 'paragraph',
      runs: [
        'Лучше всего начинать с рынков — Кур-Салея в Ницце и Прованского рынка в Антибе. Гастрономические прогулки по старым кварталам — одни из самых популярных ',
        { link: { text: 'экскурсий на Ривьере', to: 'tours' } },
        '.',
      ],
    },
    { type: 'heading', level: 3, text: 'Активный отдых на Ривьере' },
    { type: 'heading', level: 4, text: 'На воде' },
    {
      type: 'paragraph',
      runs: [
        'Выйти в море — одно из главных впечатлений Ривьеры: на лодке к Леринским островам у Канн, вокруг мыса Антиб или в бухту Вильфранша, на день к пляжу Пампелон у Сен-Тропе или посмотреть парусную регату с набережной. ',
        { link: { text: 'Арендуйте лодку или яхту', to: 'boats' } },
        ' — с капитаном или самостоятельно.',
      ],
    },
    { type: 'heading', level: 4, text: 'Пешие прогулки и велосипед' },
    {
      type: 'paragraph',
      runs: [
        { bold: 'Для любителей пеших прогулок здесь сотни километров живописных троп' },
        ' — от прибрежных тропинок мысов Кап-Ферра и Антиб и тропы Ницше, поднимающейся к Эзу, до горных маршрутов национального парка Меркантур. Велосипедисты со всего мира приезжают ради серпантинов над морем и подъёмов в предгорьях Альп.',
      ],
    },
    { type: 'heading', level: 4, text: 'Гольф' },
    {
      type: 'paragraph',
      runs: [
        { bold: 'Гольф-поля вокруг Мужена и на холмах за Каннами' },
        ' проложены среди сосновых лесов, с видом на море.',
      ],
    },
    { type: 'heading', level: 3, text: 'Вина Ривьеры и духи Граса' },
    {
      type: 'paragraph',
      runs: [
        'Ривьера граничит с Провансом — родиной лёгкого сухого розового вина, которое так подходит к лету у моря. До хозяйств Кот-де-Прованс меньше часа езды от побережья, а у Ниццы есть даже собственное крошечное наименование — Белле, одно из старейших во Франции, на холмах прямо над городом. ',
        { link: { text: 'Винные туры на Лазурном Берегу', to: 'wine-tours' } },
        ' — от мастер-класса в городе до целого дня на виноградниках.',
      ],
    },
    {
      type: 'paragraph',
      runs: [
        'В глубине материка, за Каннами, лежит Грас — ',
        { bold: 'родина французской парфюмерии' },
        ': его исторические парфюмерные дома открывают мастерские для гостей, а многие ',
        { link: { text: 'однодневные поездки', to: 'tours' } },
        ' из Ниццы и Канн объединяют парфюмерию, дегустацию вина и деревни Прованса на холмах.',
      ],
    },
    { type: 'heading', level: 3, text: 'Города Лазурного Берега' },
    {
      type: 'paragraph',
      runs: [
        'От Ментона до Сен-Тропе у каждого города побережья свой характер — виллы Belle Époque и суперъяхты, старые порты и деревни на скалах, между Альпами и Средиземным морем.',
      ],
    },
    { type: 'heading', level: 4, text: 'Ницца: культурная столица' },
    {
      type: 'paragraph',
      runs: [
        'Ницца сочетает неспешную пляжную жизнь с энергией настоящего города. Прогулка по Английской набережной — только начало: загляните в узкие улочки Старого города, на цветочный рынок Кур-Салея и в местные бистро, а также в музеи Марка Шагала и Анри Матисса. ',
        { link: { text: 'Чем заняться в Ницце', to: { city: 'nice' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Канны: кино и Круазетт' },
    {
      type: 'paragraph',
      runs: [
        'Канны известны на весь мир кинофестивалем, но красной дорожкой город не ограничивается. Пальмовый бульвар Круазетт с легендарными отелями ведёт к старому порту и холму Сюке, а в нескольких минутах на пароме — Леринские острова с древним аббатством, островок тишины. ',
        { link: { text: 'Чем заняться в Каннах', to: { city: 'cannes' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Монако: блеск на Скале' },
    {
      type: 'paragraph',
      runs: [
        'Княжеский дворец и старый город на Скале, Океанографический музей, казино Монте-Карло и повороты трассы «Формулы-1» делают крошечное княжество обязательной остановкой в любом маршруте по Ривьере. ',
        { link: { text: 'Чем заняться в Монако', to: { city: 'monaco' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Антиб: старый город и мыс Антиб' },
    {
      type: 'paragraph',
      runs: [
        'В Антибе обнесённый стенами старый город и музей Пикассо в замке Гримальди соседствуют с Порт-Вобаном — одной из крупнейших гаваней суперъяхт Средиземноморья. Вокруг мыса — прибрежная тропа Кап-д’Антиб, пляжи и джаз Жуан-ле-Пена. ',
        { link: { text: 'Чем заняться в Антибе', to: { city: 'antibes' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Сен-Тропе: богемный шарм' },
    {
      type: 'paragraph',
      runs: [
        'Когда-то скромная рыбацкая деревня, Сен-Тропе стал любимым местом художников и знаменитостей, но сохранил прованский дух: цитадель XVI века, кафе вокруг порта и длинные песчаные пляжи Пампелона. ',
        { link: { text: 'Чем заняться в Сен-Тропе', to: { city: 'sttropez' } } },
        '.',
      ],
    },
    { type: 'heading', level: 4, text: 'Ментон и Вильфранш-сюр-Мер' },
    {
      type: 'paragraph',
      runs: [
        'Ментон, последний город перед Италией, известен мягким климатом, лимонами и Праздником лимонов каждый февраль, а его старый город в итальянском стиле взбирается на холм. Вильфранш-сюр-Мер стоит на одной из самых глубоких бухт Ривьеры — с цитаделью и часовней рыбаков, расписанной Жаном Кокто. ',
        { link: { text: 'Чем заняться в Ментоне', to: { city: 'menton' } } },
        ' · ',
        { link: { text: 'Чем заняться в Вильфранш-сюр-Мер', to: { city: 'villefranche' } } },
        '.',
      ],
    },
  ],
};
